import { useEffect, useMemo, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { apiFetch } from '@/lib/apiFetch'
import { useAuth } from '@/context/AuthContext'
import TagChip from '@/components/TagChip'
import { NON_FOCUS_TAG_SLUGS } from '@/lib/voice'
import type { Tag } from '@/lib/types'

// Toast darkness runs 1–10 and is exactly the number of exercises. At 1 the
// builder picks a single well-rounded exercise.
const DARKNESS_MIN = 1
const DARKNESS_MAX = 10

function darknessLabel(darkness: number): string {
  if (darkness <= 2) return 'Barely warmed'
  if (darkness <= 4) return 'Lightly toasted'
  if (darkness <= 6) return 'Golden'
  if (darkness <= 8) return 'Well done'
  return 'Practically charcoal'
}

/** A bread slice whose crumb browns as the darkness rises (1–10). */
function BreadSlice({ darkness }: { darkness: number }) {
  const t = (darkness - DARKNESS_MIN) / (DARKNESS_MAX - DARKNESS_MIN) // 0..1
  const crumb = `hsl(${42 - t * 18} ${55 + t * 4}% ${90 - t * 58}%)`
  const crust = `hsl(${38 - t * 16} ${52 + t * 6}% ${76 - t * 50}%)`
  return (
    <svg viewBox="0 0 100 92" className="h-24 w-24" aria-hidden>
      <path
        d="M20 82 L20 44 C20 22 34 12 50 12 C66 12 80 22 80 44 L80 82 C80 85 78 87 75 87 L25 87 C22 87 20 85 20 82 Z"
        fill={crumb}
        stroke={crust}
        strokeWidth="4"
        strokeLinejoin="round"
      />
      {/* toasting spots that darken as it browns */}
      <ellipse cx="42" cy="52" rx="7" ry="5" fill={crust} opacity={t * 0.45} />
      <ellipse cx="62" cy="66" rx="6" ry="4" fill={crust} opacity={t * 0.5} />
      <ellipse cx="55" cy="38" rx="5" ry="4" fill={crust} opacity={t * 0.4} />
    </svg>
  )
}

export default function BuildPage() {
  const history = useHistory()
  const { user } = useAuth()
  const [tags, setTags] = useState<Tag[] | null>(null)
  const [error, setError] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [darkness, setDarkness] = useState(5)
  const [limitToMine, setLimitToMine] = useState(false)

  const size = darkness

  useEffect(() => {
    apiFetch<Tag[]>('/tags')
      .then(setTags)
      .catch((e) => setError(e.message))
  }, [])

  // Opt-in focuses = every tag except the four that mirror the backbone and the auto cool-down.
  const focuses = useMemo(
    () => (tags ?? []).filter((t) => !NON_FOCUS_TAG_SLUGS.includes(t.slug)),
    [tags]
  )

  const toggle = (slug: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(slug) ? next.delete(slug) : next.add(slug)
      return next
    })
  }

  const startBuild = () => {
    const params = new URLSearchParams()
    params.set('size', String(size))
    if (selected.size > 0) params.set('focuses', Array.from(selected).join(','))
    if (limitToMine) params.set('limitToMine', 'true')
    history.push(`/warmup?${params.toString()}`)
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <p className="spec-label mb-2">Voice Toaster</p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold">Warm up</h1>
      <p className="mt-2 text-muted-foreground">
        Every warm-up covers the whole instrument. Add any extra focuses you want to work on.
      </p>

      {error && <p className="mt-6 text-destructive">{error}</p>}
      {!tags && !error && <p className="mt-8 spec-label">Loading…</p>}

      {tags && (
        <>
          <section className="mt-8">
            <p className="spec-label mb-2">Focuses</p>
            <p className="mb-3 text-sm text-muted-foreground">
              Ease-in, chest, head and mix are always covered — pick any extra focuses.
            </p>
            <div className="flex flex-wrap gap-2">
              {focuses.map((t) => (
                <TagChip key={t.slug} tag={t} selected={selected.has(t.slug)} onClick={() => toggle(t.slug)} />
              ))}
            </div>
          </section>

          {user && (
            <section className="mt-8">
              <label className="flex min-h-[44px] items-center gap-2">
                <input
                  type="checkbox"
                  checked={limitToMine}
                  onChange={(e) => setLimitToMine(e.target.checked)}
                  className="h-4 w-4"
                />
                <span className="text-sm">Limit to just my warm-ups</span>
              </label>
            </section>
          )}

          <section className="mt-10 rounded-lg border bg-card p-4">
            <div className="flex items-baseline justify-between">
              <p className="spec-label">Toast darkness</p>
              <p className="text-sm font-medium">
                {darknessLabel(darkness)} · {size} exercise{size === 1 ? '' : 's'}
              </p>
            </div>
            <div className="mt-4 flex items-center gap-5">
              <BreadSlice darkness={darkness} />
              <div className="flex-1">
                <input
                  type="range"
                  min={DARKNESS_MIN}
                  max={DARKNESS_MAX}
                  step={1}
                  value={darkness}
                  onChange={(e) => setDarkness(Number(e.target.value))}
                  aria-label="Toast darkness"
                  className="w-full accent-[hsl(var(--coil))]"
                />
                <div className="mt-1 flex justify-between spec-label">
                  <span>Light</span>
                  <span className="font-mono">{darkness}/10</span>
                  <span>Dark</span>
                </div>
              </div>
            </div>
          </section>

          <button
            onClick={startBuild}
            className="mt-8 flex min-h-[52px] w-full items-center justify-center rounded-full bg-primary text-lg font-semibold text-primary-foreground shadow-lg transition-transform active:scale-[0.99]"
          >
            Build my warm-up
          </button>

          <p className="mt-4 text-center">
            <Link to="/approach" className="spec-label underline-offset-2 hover:underline">
              Why this warm-up →
            </Link>
          </p>
        </>
      )}
    </div>
  )
}
